import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PanelModule } from 'primeng/panel';
import { Editor, EditorModule } from 'primeng/editor';
import { MessageService } from 'primeng/api';
import { OutreachService } from 'src/app/services/outreach.service';
import { TemplateModel } from 'src/app/models/email-sequence.model';
import { SelectableCompany } from 'src/app/models/company.model';

@Component({
  selector: 'app-new-email-template',
  templateUrl: './new-email-template.component.html',
  styleUrls: ['./new-email-template.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    PanelModule,
    EditorModule
  ]
})
export class NewEmailTemplateComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() visible = false;
  @Input() editingTemplate = false;
  @Input() currentCompany: SelectableCompany | null = null;
  @Input() template: TemplateModel | null = null;

  @Output() templateSaved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  @ViewChild('emailEditor') editor?: Editor;

  currentTemplate: TemplateModel = {
    name: '',
    subject: '',
    body: '',
    type: 'email'
  };

  previewBody = '';
  previewData: any = {
    companyName: 'Acme Corp',
    firstName: 'John',
    lastName: 'Doe',
    title: 'Marketing Director',
    companySize: '500-1000',
    industry: 'Technology',
    accountSignature: 'Best regards,\nYour Name\nYour Title\nYour Company'
  };

  constructor(
    private messageService: MessageService,
    private emailOutreachService: OutreachService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('Template component ngOnChanges:', changes);
    if (changes['template'] || changes['editingTemplate']) {
      this.initializeForm();
    }
  }

  ngAfterViewInit(): void {
    // Try to set content after view is fully initialized
    setTimeout(() => {
      if (this.editingTemplate && this.template) {
        this.setEditorContent();
        this.cdr.detectChanges();
      }
    }, 300);
  }

  private initializeForm(): void {
    if (this.editingTemplate && this.template) {
      console.log('Initializing form with template:', this.template);
      this.currentTemplate = { ...this.template };
      
      // Set editor content after a brief delay to ensure editor is ready
      setTimeout(() => {
        this.setEditorContent();
      }, 100);
    } else {
      console.log('Resetting form for new template');
      this.resetForm();
    }
  }

  private resetForm(): void {
    this.currentTemplate = {
      name: '',
      subject: '',
      body: '',
      type: 'email'
    };
    this.previewBody = '';
  }

  insertPersonalizationVariable(variable: string): void {
    const quill = this.editor?.getQuill();
    if (quill) {
      const selection = quill.getSelection() || { index: quill.getLength(), length: 0 };
      quill.insertText(selection.index, `{{${variable}}}`);
      this.previewTemplate();
    }
  }

  private setEditorContent(): void {
    if (this.editor?.getQuill() && this.currentTemplate.body) {
      console.log('Setting editor content:', this.currentTemplate.body);
      // Use setContents for HTML content or setText for plain text
      // Since we're dealing with rich content, try both approaches
      try {
        this.editor.getQuill().root.innerHTML = this.currentTemplate.body;
      } catch (e) {
        console.warn('Failed to set HTML content, falling back to text:', e);
        this.editor.getQuill().setText(this.currentTemplate.body);
      }
    }
  }

  previewTemplate(): void {
    const quill = this.editor?.getQuill();
    if (quill) {
      let previewContent = quill.getText();

      Object.keys(this.previewData).forEach(key => {
        const variablePattern = new RegExp(`{{${key}}}`, 'g');
        previewContent = previewContent.replace(variablePattern, this.previewData[key] || '');
      });
      
      this.previewBody = previewContent;
    }
  }

  async saveTemplate(): Promise<void> {
    try {
      const quill = this.editor?.getQuill();
      if (quill) {
        // Save both plain text and HTML versions
        this.currentTemplate.body = quill.getText().trim();
        this.currentTemplate.bodyHtml = quill.root.innerHTML;
      }
      this.currentTemplate.type = 'email';

      if (this.currentTemplate.id) {
        await this.emailOutreachService.updateEmailTemplate(this.currentTemplate, this.currentCompany!.companyId);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Template updated successfully'
        });
      } else {
        await this.emailOutreachService.createEmailTemplate(this.currentTemplate, this.currentCompany!.companyId);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Template created successfully'
        });
      }

      this.templateSaved.emit();
      this.resetForm();
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save template'
      });
      console.error('Error saving template:', error);
    }
  }

  cancelTemplateEditing(): void {
    this.resetForm();
    this.cancelled.emit();
  }
}